import React, { useEffect, useMemo } from "react";
import { Check, Mail, MessageSquareText, Phone } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetVoucherReceipt } from "../../APICalling/useApiQueries";
import { BtnPrimary } from "../../Components/Buttons/BtnPrimary";
import { Footer } from "../../Components/Cart/Footer";
import LoaderMain from "../../Components/Loader/LoaderMain";
import TermsAndConditionsV2 from "../../Components/TermsAndConditionsV2/TermsAndConditionsV2";
import { useVoucherContext } from "../../Context/Voucher/VoucherContext";
import styles from "./Style.module.css";
import {
  buildVoucherReceiptGroups,
  formatMoney,
  toOptionalAmount,
} from "./voucherReceipt";

const VoucherSuccessPayment = () => {
  const navigate = useNavigate();
  const { orderReference } = useParams();
  const { selectedVouchers, clearVoucherCart, orderData } = useVoucherContext();

  // Prevent back button navigation and clear session on success
  useEffect(() => {
    // Replace current history entry so back button goes to previous page, not payment
    window.history.replaceState(null, '', window.location.href);

    // Handle browser back button
    const handlePopState = () => {
      navigate('/');
      clearVoucherCart();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, clearVoucherCart]);

  // Get order ID from URL parameter first, then fall back to context
  const orderId = orderReference || orderData?.id || orderData?.order?.id;

  // Fetch receipt data from API using order ID
  const { data: receiptResponse, isLoading, error: fetchError } = useGetVoucherReceipt(orderId);

  // Extract and normalize the receipt data to match expected format
  const rawData = receiptResponse?.data?.data;
  const order = rawData ? {
    buyer_name: rawData.billing_details?.name,
    buyer_email: rawData.billing_details?.email,
    buyer_phone: rawData.billing_details?.phone,
    recipient_name: rawData.recipient_details?.name,
    recipient_email: rawData.recipient_details?.email,
    recipient_phone: rawData.recipient_details?.phone,
    message: rawData.recipient_details?.message,
    total_price: rawData.grand_total,
    items: [
      ...((rawData.cash_vouchers?.items) || []),
      ...((rawData.service_vouchers?.items) || []),
    ],
  } : null;

  const orderItems = order?.items;
  const voucherGroups = useMemo(
    () =>
      buildVoucherReceiptGroups({
        selectedVouchers,
        orderItems,
        orderTotal: order?.total_price,
      }),
    [selectedVouchers, orderItems, order?.total_price]
  );

  const handleReturnHome = () => {
    clearVoucherCart();
    navigate("/");
  };

  const handlePurchaseAgain = () => {
    clearVoucherCart();
    navigate("/select-vouchers", { replace: true });
  };

  if (isLoading) return <LoaderMain />;

  if (fetchError || !order || voucherGroups.length === 0) {
    return (
      <main className={`${styles.errorState} container-md space-x space-y`}>
        <div className="title-main-container">
          <h1 className="title-main">We couldn&apos;t load your voucher order</h1>
          <p className="title-sub">
            {fetchError?.message || "Order details not found"}. Return home and try again.
          </p>
        </div>
        <BtnPrimary label="Return home" handleClick={handleReturnHome} />
      </main>
    );
  }

  const giftMessage =
    typeof order?.message === "string" ? order.message.trim() : "";
  const recipientName = order?.recipient_name || "the recipient";
  const computedTotal = voucherGroups.reduce(
    (total, group) => total + group.subtotal,
    0
  );
  const suppliedTotal = toOptionalAmount(order?.total_price);
  const grandTotal = suppliedTotal ?? computedTotal;

  return (
    <>
      <main
        className={`${styles.voucherSuccessPayment} container-md space-x space-y cart-space-b`}
      >
      <header className={styles.successHeader}>
        <div className={styles.successPill} role="status">
          <span className={styles.successIcon} aria-hidden="true">
            <Check size={11} strokeWidth={3} />
          </span>
          Purchase Successful!
        </div>
        <div className={styles.successContent}>
          <h1 className={styles.successTitle}>Your gift has been sent successfully</h1>
          <p className={styles.successDescription}>
            You’ll receive an invoice and confirmation. {recipientName} will
            receive a ready-to-redeem voucher by email
            {giftMessage ? " with your message" : ""}.
          </p>
        </div>
      </header>

      <div className={styles.divider} />

      <section
        className={styles.contactSection}
        aria-labelledby="billing-details-title"
      >
        <h2 id="billing-details-title" className={styles.sectionTitle}>
          Billing Details
        </h2>
        <p className={styles.contactName}>{order?.buyer_name || "—"}</p>
        <div className={styles.contactRow}>
          <Mail aria-hidden="true" />
          <span>{order?.buyer_email || "—"}</span>
        </div>
        <div className={styles.contactRow}>
          <Phone aria-hidden="true" />
          <span>{order?.buyer_phone || "—"}</span>
        </div>
      </section>

      <div className={styles.divider} />

      <section
        className={styles.contactSection}
        aria-labelledby="recipient-details-title"
      >
        <h2 id="recipient-details-title" className={styles.sectionTitle}>
          Recipient Details
        </h2>
        <p className={styles.contactName}>{order?.recipient_name || "—"}</p>
        <div className={styles.contactRow}>
          <Mail aria-hidden="true" />
          <span>{order?.recipient_email || "—"}</span>
        </div>
        <div className={styles.contactRow}>
          <Phone aria-hidden="true" />
          <span>{order?.recipient_phone || "—"}</span>
        </div>
        {giftMessage && (
          <div className={`${styles.contactRow} ${styles.messageRow}`}>
            <MessageSquareText aria-hidden="true" />
            <span>{giftMessage}</span>
          </div>
        )}
      </section>

      <div className={styles.divider} />

      <div className={styles.voucherGroups}>
        {voucherGroups.map((group) => (
          <section key={group.key} className={styles.voucherGroup}>
            <h2 className={styles.sectionTitle}>{group.name}</h2>
            <div className={styles.voucherItems}>
              {group.items.map((item) => (
                <div key={item.key} className={styles.amountRow}>
                  <span>
                    {item.quantity}x&nbsp;&nbsp;{item.name}
                  </span>
                  <strong>{formatMoney(item.lineTotal)}</strong>
                </div>
              ))}
            </div>
            <div className={styles.categorySummary}>
              {/* <div className={styles.amountRow}>
                <span>GST (9%)</span>
                <strong>{formatMoney(group.gstAmount, true)}</strong>
              </div> */}
              <div className={styles.amountRow}>
                <span>Subtotal</span>
                <strong>{formatMoney(group.subtotal)}</strong>
              </div>
            </div>
          </section>
        ))}
      </div>

      <div className={styles.totalRow}>
        <strong>Total</strong>
        <strong>{formatMoney(grandTotal)}</strong>
      </div>

      <div className={styles.divider} />

      <section className={styles.termsContainer}>
        <TermsAndConditionsV2 />
      </section>
      </main>

      <Footer
        btnLabel="Return to Homepage"
        btnClick={handlePurchaseAgain}
        isFooterActive={true}
        noTerms={true}
      />
    </>
  );
};

export default VoucherSuccessPayment;
